import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Star, Clock, Award, CheckCircle2, Calendar, BookOpen, Target, MessageSquare } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { courses } from '../mock/data';

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const course = courses.find(c => c.id === id);

  if (!course) {
    return (
      <div className="min-h-screen bg-offwhite text-navy flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-navy mb-4">Course Not Found</h1>
          <Link to="/courses">
            <Button className="bg-electric hover:bg-electric-dark text-offwhite">
              Back to Courses
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-offwhite text-navy">
      {/* Hero Section */}
      <section className="pt-28 pb-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/courses')}
            className="flex items-center text-navy/70 hover:text-navy transition-colors duration-200 mb-6"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Courses
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left - Course Info */}
            <div className="space-y-6">
              <div className="flex gap-3">
                <Badge className="bg-electric text-offwhite border-0">
                  {course.category}
                </Badge>
                <Badge className={`text-white border-0 ${
                  course.level === 'Advanced' ? 'bg-teal' : 'bg-navy'
                }`}>
                  {course.level}
                </Badge>
              </div>

              <h1 className="text-3xl sm:text-5xl font-bold text-navy leading-tight">
                {course.title}
              </h1>

              <p className="text-xl text-navy/70 leading-relaxed">
                {course.description}
              </p>

              <div className="flex flex-wrap gap-6">
                <div className="flex items-center space-x-2 text-navy/70">
                  <Clock className="w-5 h-5 text-electric" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center space-x-2 text-navy/70">
                  <Users className="w-5 h-5 text-electric" />
                  <span>{course.students}+ Students</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <span className="text-navy font-medium">{course.rating}</span>
                  <span className="text-navy/60">/5</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a
                  href="https://api.whatsapp.com/send?phone=7595888451"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button className="w-full bg-electric hover:bg-electric-dark text-offwhite py-6 text-lg">
                    Enroll Now
                  </Button>
                </a>
                <a
                  href="https://api.whatsapp.com/send?phone=7595888451"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button variant="outline" className="w-full border-2 border-navy/20 text-navy hover:bg-navy/5 py-6 px-8">
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Enquire
                  </Button>
                </a>
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative">
              <div className="relative bg-white border border-navy/10 rounded-2xl p-6 overflow-hidden">
                <img
                  src={course.image}
                  alt={course.title}
                  className="w-full rounded-2xl"
                />
                <div className="absolute top-8 right-8 bg-navy text-offwhite px-4 py-2 rounded-full font-semibold">
                  Limited Seats
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Details Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-white border border-navy/10 p-6 text-center">
              <Calendar className="w-10 h-10 text-electric mx-auto mb-3" />
              <h3 className="text-navy font-semibold mb-2">Duration</h3>
              <p className="text-navy/70">{course.duration}</p>
            </Card>
            <Card className="bg-white border border-navy/10 p-6 text-center">
              <Users className="w-10 h-10 text-electric mx-auto mb-3" />
              <h3 className="text-navy font-semibold mb-2">Batch Size</h3>
              <p className="text-navy/70">{course.batchSize} students</p>
            </Card>
            <Card className="bg-white border border-navy/10 p-6 text-center">
              <Clock className="w-10 h-10 text-electric mx-auto mb-3" />
              <h3 className="text-navy font-semibold mb-2">Schedule</h3>
              <p className="text-navy/70 text-sm">{course.schedule}</p>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Course Features */}
              <Card className="bg-white border border-navy/10 p-8">
                <h2 className="text-2xl font-bold text-navy mb-6 flex items-center">
                  <Award className="w-6 h-6 mr-3 text-electric" />
                  What You'll Get
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {course.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 mt-0.5 text-teal flex-shrink-0" />
                      <span className="text-navy/70">{feature}</span>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Curriculum */}
              <Card className="bg-white border border-navy/10 p-8">
                <h2 className="text-2xl font-bold text-navy mb-6 flex items-center">
                  <BookOpen className="w-6 h-6 mr-3 text-electric" />
                  Curriculum
                </h2>
                <div className="space-y-6">
                  {course.curriculum.map((section, idx) => (
                    <div key={idx}>
                      <h3 className="text-xl font-semibold text-electric mb-3">
                        {section.title}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {section.topics.map((topic, topicIdx) => (
                          <Badge
                            key={topicIdx}
                            className="bg-offwhite-dark text-navy border border-navy/10"
                          >
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Course Modules */}
              <Card className="bg-white border border-navy/10 p-8">
                <h2 className="text-2xl font-bold text-navy mb-6 flex items-center">
                  <Target className="w-6 h-6 mr-3 text-electric" />
                  Course Modules
                </h2>
                <div className="space-y-4">
                  {course.modules.map((module, idx) => (
                    <div
                      key={idx}
                      className="bg-offwhite-dark border border-navy/10 rounded-lg p-6 hover:border-navy/20 transition-colors duration-200"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-semibold text-navy">
                          Module {idx + 1}: {module.name}
                        </h3>
                        <Badge className="bg-electric/10 text-navy border border-navy/10">
                          {module.duration}
                        </Badge>
                      </div>
                      <p className="text-navy/70">{module.description}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Course Highlights */}
              <Card className="bg-white border border-navy/10 p-6">
                <h3 className="text-xl font-bold text-navy mb-4">Course Highlights</h3>
                <div className="space-y-3">
                  {course.highlights.map((highlight, idx) => (
                    <div key={idx} className="bg-offwhite-dark border border-navy/10 rounded-lg p-3">
                      <p className="text-navy text-sm font-medium">{highlight}</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Success Stories */}
              <Card className="bg-white border border-navy/10 p-6">
                <h3 className="text-xl font-bold text-navy mb-4">Success Stories</h3>
                <div className="space-y-4">
                  {course.testimonials.map((testimonial, idx) => (
                    <div key={idx} className="border-l-4 border-electric pl-4">
                      <h4 className="text-navy font-semibold">{testimonial.name}</h4>
                      <p className="text-navy/70 text-sm mb-2">{testimonial.rank}</p>
                      <p className="text-navy/70 text-sm italic">"{testimonial.comment}"</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Enroll Card */}
              <Card className="bg-navy border border-navy/10 p-6 sticky top-24">
                <h3 className="text-xl font-bold text-offwhite mb-3">Ready to Excel?</h3>
                <p className="text-offwhite/80 text-sm mb-4">
                  Join {course.students}+ students who are already learning
                </p>
                <a
                  href="https://api.whatsapp.com/send?phone=7595888451"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full bg-electric hover:bg-electric-dark text-offwhite py-6">
                    Enroll Now
                  </Button>
                </a>
                <p className="text-offwhite/70 text-xs mt-3 text-center">
                  Limited seats available for this batch
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseDetail;
